/**
 * jc-sale-price.js
 * 自動割引のセール価格表示（Storefront API 表示専用カート方式）
 *
 * バリアント確定時に cartCreate で1点だけの表示専用カートを作り、
 * 自動割引が適用されていれば PDP 価格エリアに
 * 「訂正線付き割引前価格・割引後価格・割引名」を表示する。
 * 顧客のカートには一切触れない。割引なし・API 失敗時は定価表示のまま（静かに劣化）。
 */

(function () {
  'use strict';

  function init() {
    const configEl = document.querySelector('script[id^="jc-sale-config-"]');
    if (!configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    if (!config.token || !config.apiVersion) return;

    const area = document.querySelector('[data-price-area]');
    if (!area) return;
    const priceEl = area.querySelector('[data-price]');
    const compareEl = area.querySelector('[data-sale-compare]');
    const noteEl = area.querySelector('[data-sale-note]');
    if (!priceEl || !compareEl || !noteEl) return;

    const QUERY = `
      mutation JcSalePrice($merchandiseId: ID!) {
        cartCreate(input: { lines: [{ merchandiseId: $merchandiseId, quantity: 1 }], buyerIdentity: { countryCode: JP } }) {
          cart {
            lines(first: 1) {
              nodes {
                cost { subtotalAmount { amount } totalAmount { amount } }
                discountAllocations { discountedAmount { amount } ... on CartAutomaticDiscountAllocation { title } }
              }
            }
          }
        }
      }
    `;

    let requestSeq = 0;
    let currentVariantId = null;
    // variantId -> { sale, salePriceYen, comparePriceYen, note } のセッション内キャッシュ
    const cache = new Map();

    /* jc-variant-picker.js の formatPrice と同一表記（¥ + 3桁区切り） */
    function formatYen(yen) {
      return `¥${yen.toLocaleString()}`;
    }

    function dispatchApplied(variantId, salePriceYen, comparePriceYen) {
      document.dispatchEvent(new CustomEvent('jc:sale-price-applied', {
        detail: { variantId: variantId, salePrice: salePriceYen, comparePrice: comparePriceYen },
      }));
    }

    /* セール表示の解除。[data-price] は触らない（applyVariant が定価を書いている） */
    function clearSale(variantId) {
      compareEl.hidden = true;
      compareEl.textContent = '';
      noteEl.hidden = true;
      noteEl.textContent = '';
      dispatchApplied(variantId, null, null);
    }

    function applyResult(result, variantId) {
      if (!result || !result.sale) {
        clearSale(variantId);
        return;
      }
      compareEl.textContent = formatYen(result.comparePriceYen);
      compareEl.hidden = false;
      priceEl.textContent = formatYen(result.salePriceYen);
      noteEl.textContent = result.note;
      noteEl.hidden = false;
      dispatchApplied(variantId, result.salePriceYen, result.comparePriceYen);
    }

    function parseYen(amount) {
      return Math.round(parseFloat(amount));
    }

    function fetchSalePrice(variantId) {
      const mySeq = ++requestSeq;

      fetch(`/api/${config.apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.token,
        },
        body: JSON.stringify({
          query: QUERY,
          variables: { merchandiseId: `gid://shopify/ProductVariant/${variantId}` },
        }),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((json) => {
          if (json.errors && json.errors.length) {
            throw new Error(json.errors[0].message || 'GraphQL error');
          }
          const node = json.data?.cartCreate?.cart?.lines?.nodes?.[0];
          if (!node) throw new Error('empty cart response');

          const subtotal = parseYen(node.cost.subtotalAmount.amount);
          const total = parseYen(node.cost.totalAmount.amount);
          const allocations = node.discountAllocations || [];
          const sale = allocations.length > 0 && total < subtotal;

          let result = { sale: false };
          if (sale) {
            const titles = allocations.map((a) => a.title).filter(Boolean);
            result = {
              sale: true,
              salePriceYen: total,
              comparePriceYen: subtotal,
              note: `${titles.join('・')}（カートで自動適用）`,
            };
          }
          cache.set(variantId, result);

          /* 古いレスポンス・既に別バリアントへ切替済みなら破棄 */
          if (mySeq !== requestSeq || variantId !== currentVariantId) return;
          applyResult(result, variantId);
        })
        .catch((err) => {
          console.warn('[jc-sale-price]', err);
          if (mySeq !== requestSeq || variantId !== currentVariantId) return;
          clearSale(variantId);
        });
    }

    function handleVariant(variantId) {
      currentVariantId = variantId;
      if (cache.has(variantId)) {
        applyResult(cache.get(variantId), variantId);
        return;
      }
      /* 取得完了までは前バリアントのセール表示を残さない */
      clearSale(variantId);
      fetchSalePrice(variantId);
    }

    /* バリアント確定・レンジ表示のフック */
    document.addEventListener('jc:price-rendered', (e) => {
      const variantId = e.detail?.variantId ?? null;
      if (variantId === null) {
        /* レンジ表示（包装未選択）→ セール表示解除 */
        currentVariantId = null;
        requestSeq++;
        clearSale(null);
        return;
      }
      handleVariant(variantId);
    });

    /* Dawn セクション再取得完了後の再適用（スティッキーバーが元価格へ戻る競合への対策）。
       キャッシュ済みの結果がある場合のみ再適用し、API は叩かない */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        if (currentVariantId && cache.has(currentVariantId)) {
          applyResult(cache.get(currentVariantId), currentVariantId);
        }
      });
    }

    /* 初期表示: バリアントが既に確定している場合のみ即時取得。
       - ?variant= 付き遷移（LP から）や単一バリアント商品が該当
       - 2軸商品の包装未選択（レンジ表示 = --range/--pending）では取得しない */
    const variantInput = document.querySelector('input[name="id"]');
    const isRange = area.classList.contains('jc-vp__price-area--range')
      || area.classList.contains('jc-vp__price-area--pending');
    if (variantInput && variantInput.value && !isRange) {
      handleVariant(parseInt(variantInput.value, 10));
    }
  }

  /* defer 実行時点は readyState 'interactive'（DOMContentLoaded 前）。
     jc-variant-picker.js（先に登録）→ 本スクリプトの順で DOMContentLoaded 後に
     初期化することで、ピッカーのレンジ状態確定後に初期フェッチ判定を行う */
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
